import {Component, Input, OnChanges, SimpleChanges, ViewChild} from '@angular/core';
import {IconType, IoniconsFilled, IoniconsOutline} from '@pt/pt-models';
import {WidgetTreatmentDataInterestedPreviewDataV0} from './models/widget-treatment-data-interested-preview-data-v0';
import { Observable } from 'rxjs';

@Component({
  selector: 'pt-widget-treatment-data-interested-v0-preview',
  template: `
    <ng-container *ngIf='loading; then LOADING; else PREVIEW'></ng-container>

    <ng-template #LOADING>
      Loading ...
    </ng-template>

    <ng-template #PREVIEW>

      <ng-container *ngIf='noData; then NODATA; else DATA'></ng-container>

      <ng-template #NODATA>
        <pt-widget-nodata [noDataLabel]="noDataLabel"></pt-widget-nodata>
      </ng-template>

      <ng-template #DATA>
        <div class='wtdi-preview' [ngClass]='{"expand": !isCollapsed, "reduce": isCollapsed}'>

          <!-- ROWS -->
          <div class='wtdi-preview-rows'>
            <p class='fw-bold mb-2'>{{ previewData?.rowTitle }}</p>
            <ng-container *ngIf='previewData.rowGroupByCategory; then ROW_CATEGORY; else ROW_LIST'></ng-container>
            <ng-template #ROW_CATEGORY>
              <ul class='wtdi-list-group' *ngIf='rowCategory'>
                <ngb-accordion #acc='ngbAccordion'>
                  <ng-container *ngFor='let category of rowCategory'>
                    <ngb-panel id='{{groupIdPrefix + category}}' cardClass='panel-group'>
                      <ng-template ngbPanelHeader>
                        <div class='wtdi-list-group--header cursor-pointer'
                             (click)='expandGroup(groupIdPrefix + category)'>
                          <pt-icon [size]='12'
                                   [key]='acc?.isExpanded(groupIdPrefix + category) ? IconCaretDown : IconCaretRight'></pt-icon>
                          <pt-icon [key]='IconCheckboxFilled' class='widget-icon-preview-color ms-1'></pt-icon>
                          <span class='ms-1'>{{ category }}</span>
                        </div>
                      </ng-template>
                      <ng-template ngbPanelContent>
                        <ng-container *ngFor='let item of previewData?.row'>
                          <li class='wtdi-list-group--category-item' *ngIf='item.category === category'>
                            <pt-icon [key]='IconCheckboxFilled' class='widget-icon-preview-color'></pt-icon>
                            <span class='ms-1'>{{ item.label }}</span>
                            <pt-w-help [help]='item?.help'></pt-w-help>
                          </li>
                        </ng-container>
                      </ng-template>
                    </ngb-panel>
                  </ng-container>
                </ngb-accordion>
              </ul>
            </ng-template>
            <ng-template #ROW_LIST>
              <ul class='list-unstyled m-0'>
                <ng-container *ngFor='let item of previewData?.row'>
                  <li class='d-flex'>
                    <pt-icon [key]='IconCheckboxFilled' class='widget-icon-preview-color'></pt-icon>
                    <span class='ms-1'>{{ item.label }}</span>
                    <pt-w-help [help]='item?.help'></pt-w-help>
                  </li>
                </ng-container>
              </ul>
            </ng-template>
          </div>

          <!-- COLS -->
          <div class='wtdi-preview-cols'>
            <p class='fw-bold m-0'>{{ previewData?.colTitle }}</p>
            <ng-container *ngIf='previewData.colGroupByCategory; then COL_CATEGORY; else COL_LIST'></ng-container>
            <ng-template #COL_CATEGORY>
              <ul class='list-unstyled m-0'>
                <ng-container *ngFor='let category of colCategory'>
                  <li class='fw-bold'>{{ category }}</li>
                  <ng-container *ngFor='let item of previewData?.col'>
                    <li *ngIf='item.category === category'>
                      <pt-icon [key]='IconCheckboxFilled' class='widget-icon-preview-color'></pt-icon>
                      <span class='ms-1'>{{ item.label }}</span>
                      <pt-w-help [help]='item?.help'></pt-w-help>
                    </li>
                  </ng-container>
                </ng-container>
              </ul>
            </ng-template>
            <ng-template #COL_LIST>
              <ul class='wtdi-list' *ngIf='previewData?.col'>
                <ng-container *ngFor='let item of previewData?.col'>
                  <li class='my-2'>
                    <pt-icon [key]='IconCheckboxFilled' class='widget-icon-preview-color'></pt-icon>
                    <span class='ms-1'>{{ item.label }}</span>
                    <pt-w-help [help]='item?.help'></pt-w-help>
                  </li>
                </ng-container>
              </ul>
            </ng-template>
          </div>
        </div>

        <div class='toggle-container mt-2' (click)='isCollapsed = !isCollapsed'
             ngbTooltip="{{(isCollapsed ? 'LABELS.EXPAND' : 'LABELS.REDUCE') | translate}}">
        <span class='btn btn-sm btn-toggle w-100' [ngClass]="{'flip-icon-toggle': !isCollapsed}">
          <pt-icon [size]='12'
                   [key]='IconArrow'
                   [iconType]='IconType'
          ></pt-icon>
        </span>
        </div>

        <div class='wtdi-preview-table-container'>
          <pt-widget-treatment-data-interested-v0-table
            [row]='previewData?.row'
            [col]='previewData?.col'
            [groupByCategory]='previewData.rowGroupByCategory'
            [preview]='true'
            [cells]='previewData?.cells'>
          </pt-widget-treatment-data-interested-v0-table>
        </div>
      </ng-template>
    </ng-template>
  `,
  styleUrls: [`./widget-treatment-data-interested-v0-preview.component.scss`]
})
export class WidgetTreatmentDataInterestedV0PreviewComponent implements OnChanges {

  public readonly groupIdPrefix = 'widget-treatment-data-interested-category-preview_';

  @ViewChild('acc') acc;

  @Input() noDataLabel?: string;
  @Input() previewData?: WidgetTreatmentDataInterestedPreviewDataV0;
  @Input() loading: boolean;

  noData = true;
  isCollapsed = null;

  IconCheckboxFilled = IoniconsFilled.checkbox;
  IconArrowDown = IoniconsFilled.caretDown;
  IconCaretRight = IoniconsFilled.caretRight;
  IconCaretDown = IoniconsFilled.caretDown;
  IconCheck = IoniconsFilled.check;
  IconArrow = IoniconsOutline.arrowDown;
  IconType = IconType.IONIC_ICON;

  colCategory: string[];
  rowCategory: string[];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.previewData) {
      this.noData = !(this.previewData?.col?.length > 0) && !(this.previewData?.row?.length > 0);

      this.colCategory = [];
      if (this.previewData?.colGroupByCategory) {
        this.previewData?.col.forEach(c => {
          if (!this.colCategory.includes(c.category)) {
            this.colCategory.push(c.category);
          }
        });
      }

      this.rowCategory = [];
      if (this.previewData?.rowGroupByCategory) {
        this.previewData?.row.forEach(c => {
          if (!this.rowCategory.includes(c.category)) {
            this.rowCategory.push(c.category);
          }
        });
      }
    }
  }

  expandGroup(groupId: string) {
    if (groupId) {
      if (!this.acc.isExpanded(groupId)) {
        this.acc.expand(groupId);
      } else {
        this.acc.collapse(groupId);
      }
    }
  }
}
